import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// Create Admin (sends OTP)
export const CreateAdmin = createAsyncThunk(
  "adminOTP/createAdmin",
  async (adminData, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      const response = await axios.post(
        `${BASE_URL}/admin/admins`,
        adminData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      // Handle both original axios error and interceptor modified error
      const message = error.data?.message || error.response?.data?.message || error.message || "Failed to create admin";
      const errors = error.data?.errors || error.response?.data?.errors;
      return rejectWithValue({ message, errors });
    }
  }
);

// Verify Admin OTP
export const VerifyAdminOTP = createAsyncThunk(
  "adminOTP/verifyOTP",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/admin/admins/verify-otp`, {
        email,
        otp,
      });
      return { data: response.data, status: response.status };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "OTP verification failed",
        attemptsRemaining: error.response?.data?.attemptsRemaining,
      });
    }
  }
);

// Resend Admin OTP (Super Admin only)
export const ResendAdminOTP = createAsyncThunk(
  "adminOTP/resendOTP",
  async ({ adminId }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      const response = await axios.post(
        `${BASE_URL}/admin/admins/resend-otp`,
        { adminId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to resend OTP",
        cooldownRemaining: error.response?.data?.cooldownRemaining,
      });
    }
  }
);

// Get All Admins
export const GetAllAdmins = createAsyncThunk(
  "adminOTP/getAllAdmins",
  async (_, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      const response = await axios.get(`${BASE_URL}/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { data: response.data, status: response.status };
    } catch (error) {
      // Handle both original axios error and interceptor modified error
      const message = error.data?.message || error.response?.data?.message || error.message || "Failed to fetch admins";
      return rejectWithValue({ message });
    }
  }
);

// Update Admin
export const UpdateAdmin = createAsyncThunk(
  "adminOTP/updateAdmin",
  async ({ adminId, data }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      const response = await axios.put(
        `${BASE_URL}/admin/admins/${adminId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to update admin",
      });
    }
  }
);

// Delete Admin
export const DeleteAdmin = createAsyncThunk(
  "adminOTP/deleteAdmin",
  async ({ adminId }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      const response = await axios.delete(`${BASE_URL}/admin/admins/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { data: response.data, status: response.status, adminId };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to delete admin",
      });
    }
  }
);

// Toggle Admin Status
export const ToggleAdminStatus = createAsyncThunk(
  "adminOTP/toggleStatus",
  async ({ adminId }, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("ots_token") || localStorage.getItem("ots_token");
      const response = await axios.put(
        `${BASE_URL}/admin/admins/${adminId}/toggle-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { data: response.data, status: response.status };
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Failed to toggle admin status",
      });
    }
  }
);

const AdminOTP = createSlice({
  name: "adminOTP",
  initialState: {
    loading: false,
    admins: [],
    pendingAdmin: null, // Admin waiting for OTP verification
    otpSent: false,
    otpVerified: false,
    error: "",
    successMessage: "",
  },
  reducers: {
    resetAdminOTPState: (state) => {
      state.loading = false;
      state.pendingAdmin = null;
      state.otpSent = false;
      state.otpVerified = false;
      state.error = "";
      state.successMessage = "";
    },
    clearError: (state) => {
      state.error = "";
    },
    clearSuccess: (state) => {
      state.successMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Admin
      .addCase(CreateAdmin.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(CreateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        // Handle different response structures
        const responseData = action.payload?.data;
        state.pendingAdmin = responseData?.data?.admin || responseData?.data || responseData?.admin || responseData;
        state.otpSent = true;
        state.successMessage = responseData?.message || responseData?.data?.message || "OTP sent to admin email";
      })
      .addCase(CreateAdmin.rejected, (state, action) => {
        state.loading = false;
        state.otpSent = false;
        state.error = action.payload?.message || "Failed to create admin";
      })
      // Verify OTP
      .addCase(VerifyAdminOTP.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(VerifyAdminOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.otpVerified = true;
        state.pendingAdmin = null;
        state.otpSent = false;
        state.successMessage = action.payload.data.message || "Admin verified successfully";
      })
      .addCase(VerifyAdminOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "OTP verification failed";
      })
      // Resend OTP
      .addCase(ResendAdminOTP.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(ResendAdminOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.data.message || "OTP resent successfully";
      })
      .addCase(ResendAdminOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to resend OTP";
      })
      // Get All Admins
      .addCase(GetAllAdmins.pending, (state) => {
        state.loading = true;
      })
      .addCase(GetAllAdmins.fulfilled, (state, action) => {
        state.loading = false;
        // Handle different response structures
        // API returns: { success: true, data: { admins: [...], pagination: {...}, stats: {...} } }
        const responseData = action.payload?.data;
        if (Array.isArray(responseData)) {
          state.admins = responseData;
        } else if (Array.isArray(responseData?.data?.admins)) {
          // New API structure: data.data.admins
          state.admins = responseData.data.admins;
        } else if (Array.isArray(responseData?.admins)) {
          // Alternative: data.admins
          state.admins = responseData.admins;
        } else if (Array.isArray(responseData?.data)) {
          // Old structure: data.data is array
          state.admins = responseData.data;
        } else {
          state.admins = [];
        }
      })
      .addCase(GetAllAdmins.rejected, (state, action) => {
        state.loading = false;
        state.admins = [];
        state.error = action.payload?.message;
      })
      // Update Admin
      .addCase(UpdateAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Admin updated successfully";
        // Update admin in list - handle different response structures
        const updatedAdmin = action.payload?.data?.data || action.payload?.data;
        if (updatedAdmin && updatedAdmin._id) {
          state.admins = (state.admins || []).map((admin) =>
            admin._id === updatedAdmin._id ? updatedAdmin : admin
          );
        }
      })
      // Delete Admin
      .addCase(DeleteAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Admin deleted successfully";
        state.admins = (state.admins || []).filter((admin) => admin._id !== action.payload.adminId);
      })
      // Toggle Status
      .addCase(ToggleAdminStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedAdmin = action.payload?.data?.data || action.payload?.data;
        if (updatedAdmin && updatedAdmin._id) {
          state.admins = (state.admins || []).map((admin) =>
            admin._id === updatedAdmin._id ? updatedAdmin : admin
          );
        }
      });
  },
});

export const { resetAdminOTPState, clearError, clearSuccess } = AdminOTP.actions;
export default AdminOTP.reducer;
